import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import ROUTES from 'constants/routes';
import { RESET_PASSWORD } from 'constants/data-testid';
import Panel from 'components/Panel';
import Grid from '@material-ui/core/Grid';
import { FieldError, FieldGroup } from 'components/Field';
import TextField, { FIELD_TYPES } from 'components/Mui/TextField';
import TextColored, { textColors } from 'components/TextColored';
import AppLogo, { appLogoVersions } from 'components/AppLogo';
import { BRIGHT_GRAY } from 'constants/colors';
import Button, { buttonColors, ButtonGroup, buttonSizes } from 'components/Button';
import Form from 'components/Form';
import _isEmpty from 'lodash/isEmpty';
import Box from '@material-ui/core/Box';
import { FormWrap, LogoWrap, FormLink } from '../styles';
import { exitContainer, initContainer, sendRecoveryLink } from './actions';
import { getExpiredLink, getIsSubmitting } from './selectors';

const ResetPassword = ({
  resetPasswordError,
  goToLogin,
  onSendRecoveryLink,
  isSubmitting,
  onExit,
  onEnter,
  hasExpired,
}) => {
  const [account_id, setAccountId] = useState('');
  const [username, setUsername] = useState('');
  const [formError, setFormError] = useState(null);

  const handleOnUsernameChange = value => {
    setFormError(null);
    setUsername(value);
  };

  const handleOnSubmit = () => {
    if (!_isEmpty(account_id) && !_isEmpty(username)) {
      onSendRecoveryLink({ account_id, username });
      setUsername('');
    }
  };

  useEffect(() => {
    onEnter();

    return () => {
      onExit();
    };
  }, [onEnter, onExit]);

  return (
    <Panel data-testid={RESET_PASSWORD.FORGOT_PASSWORD_CONTAINER}>
      <FormWrap>
        <LogoWrap>
          <AppLogo color={BRIGHT_GRAY} version={appLogoVersions.v2} link='https://commerceblitz.com' />
        </LogoWrap>
        <Form onSubmit={handleOnSubmit}>
          <Grid
            container
            justifyContent="center"
            style={{ padding: '1rem 0' }}
          >
            <h2>Forgot your password?</h2>
          </Grid>
          {hasExpired && (
            <TextColored color={textColors.danger} padding="0 0 1rem 0">
              <h4>The recovery link has expired. You can request a new link.</h4>
            </TextColored>
          )}
          <Grid
            container
            style={{ padding: '0 0 0.5rem 0' }}
          >
            <span>Please enter your Account ID and Username and we will send a recovery link to your email.
            </span>
          </Grid>
          <Grid
            container
            style={{ padding: '0 0 0.5rem 0' }}
          >
            <FieldGroup>
              <Box
                display="flex"
                flexDirection="column"
                gridRowGap="1rem"
                width="100%"
              >
                <TextField
                  variant="outlined"
                  type={FIELD_TYPES.TEXT}
                  label="Account ID"
                  value={account_id}
                  onChange={setAccountId}
                  inputProps={{
                    'data-testid': RESET_PASSWORD.FIELD_ACCOUNT_ID,
                  }}
                  autoComplete
                  fullWidth
                />
                <TextField
                  variant="outlined"
                  type={FIELD_TYPES.TEXT}
                  label="Username"
                  value={username}
                  onChange={handleOnUsernameChange}
                  error={formError}
                  inputProps={{
                    'data-testid': RESET_PASSWORD.FIELD_USERNAME,
                  }}
                  autoComplete
                  fullWidth
                />
              </Box>
            </FieldGroup>
          </Grid>

          <Grid container>
            <ButtonGroup>
              <Button
                type="submit"
                color={buttonColors.info}
                size={buttonSizes.large}
                fullWidth
                loading={isSubmitting}
                disabled={isSubmitting}
                data-testid={RESET_PASSWORD.BTN_SEND_LINK}
              >
                <span>Send Recovery Link</span>
              </Button>
            </ButtonGroup>
          </Grid>
          {resetPasswordError && (
            <FieldError data-testid={RESET_PASSWORD.MESSAGE_ERROR}>
              <strong>{resetPasswordError}</strong>
            </FieldError>
          )}
          <Grid
            container
            justifyContent="center"
            style={{ padding: '1rem 0 0.5rem 0' }}
          >
            <span>
              Already have login and password? &nbsp;
            </span>
            <FormLink
              onClick={() => goToLogin()}
              data-testid={RESET_PASSWORD.LINK_SIGN_IN}
            >
              Sign in
            </FormLink>
          </Grid>
        </Form>
      </FormWrap>
    </Panel>
  );
};

ResetPassword.propTypes = {
  resetPasswordError: PropTypes.string,
  goToLogin: PropTypes.func.isRequired,
  onSendRecoveryLink: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onExit: PropTypes.func.isRequired,
  onEnter: PropTypes.func.isRequired,
  hasExpired: PropTypes.bool.isRequired,
};

ResetPassword.defaultProps = {
  resetPasswordError: null,
};

const mapStateToProps = state => ({
  isSubmitting: getIsSubmitting(state),
  hasExpired: getExpiredLink(state),
});

const mapDispatchToProps = {
  goToLogin: () => push(ROUTES.LOGIN.BASE),
  onSendRecoveryLink: sendRecoveryLink,
  onEnter: initContainer,
  onExit: exitContainer,
};

export default connect(mapStateToProps, mapDispatchToProps)(ResetPassword);
